import React, { useState } from 'react'
import { Modal, Typography } from '@mui/material'
import { Formik, Form } from 'formik'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { CreateContractorFile } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { useDispatch } from 'react-redux'
import { CreateContractorFileSchema } from './CreateContractorFileForm.validator'
import { CreateContractorFileForm } from './CreateContractorFileForm.interface'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { colors } from 'src/theme'
import { FormikFileInput } from 'src/components/shared/form/formik/FormikFileInput'

interface Props {
  isOpen: boolean
  contractorId: string
  onClose(): void
  onUpdate(): void | Promise<void>
}

const initialValues: CreateContractorFileForm = {
  name: '',
  file: '',
}

export const CreateContractorFileModal = ({ isOpen, contractorId, onClose, onUpdate }: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (values: CreateContractorFileForm) => {
    try {
      setIsLoading(true)

      await createAsyncAction(
        dispatch,
        CreateContractorFile.request({
          contractorId,
          file: values.file,
          name: values.name,
        }),
      )

      showToast('File has been added!')

      onClose()
      await onUpdate()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="file-modal-title"
      aria-describedby="file-modal-description"
      css={tw`fixed inset-0 flex items-center justify-center`}
    >
      <div
        data-testid="contractor-details-add-file-modal"
        css={tw`w-[336px] bg-white flex flex-col rounded-lg shadow-lg`}
      >
        <ModalHeader
          className={tw`h-11`}
          title="Add file"
          onClose={onClose}
          closeButtonDataTestId="contractor-details-add-file-modal-close-btn"
        />
        <Formik
          initialValues={initialValues}
          validationSchema={CreateContractorFileSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form css={tw`flex flex-col flex-1`}>
              <div css={tw`p-4 flex-1 overflow-y-auto`}>
                <FormikTextField
                  name="name"
                  fullWidth
                  label="Name"
                  placeholder="Enter custom name"
                />
                <Typography variant="h6" color={colors.SHADES_GREY_400} css={tw`mb-1.5 mt-4`}>
                  Upload file*
                </Typography>
                <FormikFileInput name="file" />
              </div>
              <div
                css={tw`px-4 py-3 flex justify-between border-0 border-t border-solid border-[#EDEDED]`}
              >
                <Button
                  variant="contained"
                  color="grey"
                  size="small"
                  onClick={onClose}
                  loading={isLoading}
                  className={tw`w-[100px]`}
                  data-testid="contractor-details-add-file-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  loading={isLoading}
                  className={tw`w-[120px]`}
                  data-testid="contractor-details-add-file-submit-btn"
                >
                  Add
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}
